/**
 * Page de finalisation de commande e-commerce
 * Formulaire client + choix livraison/paiement + validation
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatPrice } from '../utils/pos.utils';
import { useCart } from '../contexts/CartContext';

interface CustomerInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  notes?: string;
}

type DeliveryMethod = 'pickup' | 'delivery' | 'shipping';
type PaymentMethod = 'card' | 'cash' | 'check' | 'transfer';

export default function Checkout() {
  const navigate = useNavigate();
  const { cart, clearCart } = useCart();
  const [step, setStep] = useState<1 | 2 | 3>(1); // 1: Info client, 2: Livraison/Paiement, 3: Confirmation
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Informations client
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'France',
    notes: '',
  });

  // Méthodes de livraison et paiement
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>('pickup');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');

  // Calculs
  const subtotal = cart.reduce((total, item) => {
    const price = item.product.prices?.[0]?.amount_cents ?? 0;
    return total + price * item.quantity;
  }, 0);

  const deliveryCost = deliveryMethod === 'shipping' ? 890 : deliveryMethod === 'delivery' ? 490 : 0;
  const total = subtotal + deliveryCost;

  const handleCustomerInfoChange = (field: keyof CustomerInfo, value: string) => {
    setCustomerInfo({ ...customerInfo, [field]: value });
  };

  const validateStep1 = (): boolean => {
    if (!customerInfo.firstName || !customerInfo.lastName) {
      setError('Veuillez renseigner votre nom et prénom');
      return false;
    }
    if (!customerInfo.email) {
      setError('Veuillez renseigner votre email');
      return false;
    }
    if (!customerInfo.phone) {
      setError('Veuillez renseigner votre téléphone');
      return false;
    }
    if (deliveryMethod !== 'pickup' && (!customerInfo.address || !customerInfo.city || !customerInfo.postalCode)) {
      setError('Veuillez renseigner votre adresse complète');
      return false;
    }
    setError(null);
    return true;
  };

  const handleNextStep = () => {
    if (step === 1 && !validateStep1()) {
      return;
    }
    setStep((step + 1) as 2 | 3);
  };

  const handlePreviousStep = () => {
    setStep((step - 1) as 1 | 2);
  };

  const handleSubmitOrder = async () => {
    setSubmitting(true);
    setError(null);

    try {
      // Préparer les données de commande
      const orderData = {
        customer: customerInfo,
        items: cart.map((item) => ({
          product_id: item.product.id,
          quantity: item.quantity,
          price_cents: item.product.prices?.[0]?.amount_cents ?? 0,
        })),
        delivery_method: deliveryMethod,
        payment_method: paymentMethod,
        subtotal_cents: subtotal,
        delivery_cents: deliveryCost,
        total_cents: total,
      };

      // Envoyer la commande à l'API
      const response = await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la création de la commande');
      }

      const order = await response.json();

      // Succès - vider le panier et rediriger
      clearCart();
      alert(`✅ Commande #${order.id || 'XXX'} créée avec succès!\n\nVous recevrez un email de confirmation à ${customerInfo.email}`);
      navigate('/shop');
    } catch (err) {
      setError(`Erreur: ${(err as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <svg
            className="mx-auto h-16 w-16 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <h2 className="mt-4 text-2xl font-bold text-gray-900">Votre panier est vide</h2>
          <p className="mt-2 text-gray-500">Ajoutez des produits avant de passer commande</p>
          <button
            onClick={() => navigate('/shop')}
            className="mt-6 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retour à la boutique
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Finaliser ma commande</h1>
            <button
              onClick={() => navigate('/shop')}
              className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Retour
            </button>
          </div>

          {/* Progress Steps */}
          <div className="mt-6 flex items-center justify-center">
            <div className="flex items-center space-x-4">
              {/* Step 1 */}
              <div className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                  }`}
                >
                  1
                </div>
                <span className="ml-2 text-sm font-medium text-gray-900">Coordonnées</span>
              </div>

              <div className="w-16 h-1 bg-gray-300"></div>

              {/* Step 2 */}
              <div className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                  }`}
                >
                  2
                </div>
                <span className="ml-2 text-sm font-medium text-gray-900">Livraison</span>
              </div>

              <div className="w-16 h-1 bg-gray-300"></div>

              {/* Step 3 */}
              <div className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                  }`}
                >
                  3
                </div>
                <span className="ml-2 text-sm font-medium text-gray-900">Confirmation</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow p-6">
              {/* Step 1: Customer Info */}
              {step === 1 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">Vos coordonnées</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Prénom *
                      </label>
                      <input
                        type="text"
                        value={customerInfo.firstName}
                        onChange={(e) => handleCustomerInfoChange('firstName', e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom *
                      </label>
                      <input
                        type="text"
                        value={customerInfo.lastName}
                        onChange={(e) => handleCustomerInfoChange('lastName', e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={customerInfo.email}
                        onChange={(e) => handleCustomerInfoChange('email', e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Téléphone *
                      </label>
                      <input
                        type="tel"
                        value={customerInfo.phone}
                        onChange={(e) => handleCustomerInfoChange('phone', e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mode de livraison *
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center p-3 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          value="pickup"
                          checked={deliveryMethod === 'pickup'}
                          onChange={(e) => setDeliveryMethod(e.target.value as DeliveryMethod)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-900">
                          Retrait en magasin (gratuit)
                        </span>
                      </label>
                      <label className="flex items-center p-3 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          value="delivery"
                          checked={deliveryMethod === 'delivery'}
                          onChange={(e) => setDeliveryMethod(e.target.value as DeliveryMethod)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-900">
                          Livraison locale ({formatPrice(490)})
                        </span>
                      </label>
                      <label className="flex items-center p-3 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          value="shipping"
                          checked={deliveryMethod === 'shipping'}
                          onChange={(e) => setDeliveryMethod(e.target.value as DeliveryMethod)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-900">
                          Livraison nationale ({formatPrice(890)})
                        </span>
                      </label>
                    </div>
                  </div>

                  {deliveryMethod !== 'pickup' && (
                    <div className="space-y-4">
                      <h3 className="font-medium text-gray-900">Adresse de livraison</h3>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Adresse *
                        </label>
                        <input
                          type="text"
                          value={customerInfo.address}
                          onChange={(e) => handleCustomerInfoChange('address', e.target.value)}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Code postal *
                          </label>
                          <input
                            type="text"
                            value={customerInfo.postalCode}
                            onChange={(e) => handleCustomerInfoChange('postalCode', e.target.value)}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Ville *
                          </label>
                          <input
                            type="text"
                            value={customerInfo.city}
                            onChange={(e) => handleCustomerInfoChange('city', e.target.value)}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Pays *
                        </label>
                        <input
                          type="text"
                          value={customerInfo.country}
                          onChange={(e) => handleCustomerInfoChange('country', e.target.value)}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes (optionnel)
                    </label>
                    <textarea
                      value={customerInfo.notes}
                      onChange={(e) => handleCustomerInfoChange('notes', e.target.value)}
                      rows={3}
                      placeholder="Instructions de livraison, allergies, etc."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Payment */}
              {step === 2 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">Mode de paiement</h2>

                  <div className="space-y-2">
                    <label className="flex items-center p-4 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        value="card"
                        checked={paymentMethod === 'card'}
                        onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="ml-3">
                        <span className="block text-sm font-medium text-gray-900">
                          Carte bancaire
                        </span>
                        <span className="block text-xs text-gray-500">
                          Paiement sécurisé par Stripe
                        </span>
                      </div>
                    </label>

                    <label className="flex items-center p-4 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        value="cash"
                        checked={paymentMethod === 'cash'}
                        onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="ml-3">
                        <span className="block text-sm font-medium text-gray-900">
                          Espèces
                        </span>
                        <span className="block text-xs text-gray-500">
                          Paiement à la livraison ou au retrait
                        </span>
                      </div>
                    </label>

                    <label className="flex items-center p-4 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        value="check"
                        checked={paymentMethod === 'check'}
                        onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="ml-3">
                        <span className="block text-sm font-medium text-gray-900">
                          Chèque
                        </span>
                        <span className="block text-xs text-gray-500">
                          À remettre au retrait
                        </span>
                      </div>
                    </label>

                    <label className="flex items-center p-4 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        value="transfer"
                        checked={paymentMethod === 'transfer'}
                        onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="ml-3">
                        <span className="block text-sm font-medium text-gray-900">
                          Virement bancaire
                        </span>
                        <span className="block text-xs text-gray-500">
                          Coordonnées envoyées par email
                        </span>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Step 3: Confirmation */}
              {step === 3 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">Confirmation de commande</h2>

                  {/* Récapitulatif client */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Vos coordonnées</h3>
                    <div className="bg-gray-50 p-4 rounded-md text-sm">
                      <p className="font-medium">
                        {customerInfo.firstName} {customerInfo.lastName}
                      </p>
                      <p className="text-gray-600">{customerInfo.email}</p>
                      <p className="text-gray-600">{customerInfo.phone}</p>
                    </div>
                  </div>

                  {/* Récapitulatif livraison */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Livraison</h3>
                    <div className="bg-gray-50 p-4 rounded-md text-sm">
                      <p className="font-medium">
                        {deliveryMethod === 'pickup'
                          ? 'Retrait en magasin'
                          : deliveryMethod === 'delivery'
                          ? 'Livraison locale'
                          : 'Livraison nationale'}
                      </p>
                      {deliveryMethod !== 'pickup' && (
                        <p className="text-gray-600 mt-1">
                          {customerInfo.address}<br />
                          {customerInfo.postalCode} {customerInfo.city}<br />
                          {customerInfo.country}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Récapitulatif paiement */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Paiement</h3>
                    <div className="bg-gray-50 p-4 rounded-md text-sm">
                      <p className="font-medium">
                        {paymentMethod === 'card'
                          ? 'Carte bancaire'
                          : paymentMethod === 'cash'
                          ? 'Espèces'
                          : paymentMethod === 'check'
                          ? 'Chèque'
                          : 'Virement bancaire'}
                      </p>
                    </div>
                  </div>

                  {/* Notes */}
                  {customerInfo.notes && (
                    <div>
                      <h3 className="font-medium text-gray-900 mb-2">Notes</h3>
                      <div className="bg-gray-50 p-4 rounded-md text-sm">
                        <p className="text-gray-600">{customerInfo.notes}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="mt-8 flex items-center justify-between">
                {step > 1 && (
                  <button
                    onClick={handlePreviousStep}
                    className="px-6 py-2 text-gray-700 hover:text-gray-900 flex items-center gap-2"
                    disabled={submitting}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Retour
                  </button>
                )}

                {step < 3 ? (
                  <button
                    onClick={handleNextStep}
                    className="ml-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    Continuer
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ) : (
                  <button
                    onClick={handleSubmitOrder}
                    disabled={submitting}
                    className="ml-auto px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Validation en cours...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        Valider la commande
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 sticky top-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Récapitulatif</h3>

              {/* Cart Items */}
              <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                {cart.map((item) => {
                  const price = item.product.prices?.[0]?.amount_cents ?? 0;
                  const subtotal = price * item.quantity;

                  return (
                    <div key={item.product.id} className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gray-100 rounded flex-shrink-0">
                        {item.product.image_url ? (
                          <img
                            src={item.product.image_url}
                            alt={item.product.name}
                            className="w-full h-full object-cover rounded"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{item.product.name}</p>
                        <p className="text-sm text-gray-500">Qté: {item.quantity}</p>
                      </div>
                      <p className="text-sm font-medium text-gray-900">{formatPrice(subtotal)}</p>
                    </div>
                  );
                })}
              </div>

              {/* Totals */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Sous-total</span>
                  <span className="font-medium text-gray-900">{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Livraison</span>
                  <span className="font-medium text-gray-900">
                    {deliveryCost === 0 ? 'Gratuit' : formatPrice(deliveryCost)}
                  </span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span className="text-gray-900">Total</span>
                  <span className="text-green-600">{formatPrice(total)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
